var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from "react";
import { observer } from "mobx-react";
import { Trans } from "react-i18next";
import { message } from "antd";
import { pick, isEqual } from "lodash";
import { Permissions } from "@whatsper/texterchat-common";
import { useStore } from "../../../../store/rootStore";
import { Operation } from "../../../EntityManager";
import { ManagerDetailsView } from "../core";
import AgentForm from "./components/AgentForm";
import { agentPropertiesEditable } from "./components/helpers";
const AgentsDetailsView = observer(({ entityId, operation, onCancel, onCompleted, }) => {
    const { agentsStore, channels } = useStore();
    const [loading, setLoading] = useState(false);
    const entity = (() => {
        if (!entityId || operation === Operation.CREATE)
            return null;
        return agentsStore.users.find((u) => u._id === entityId) || null;
    })();
    const getName = (agent) => {
        return agent.displayName || agent.email || agent.uid;
    };
    const renderDeleteQuestion = (agent) => {
        return React.createElement(Trans, { i18nKey: "sureDeleteName", values: { name: getName(agent) } });
    };
    const handleSave = (formData, existingAgent) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        setLoading(true);
        let userSaved = false;
        try {
            if (existingAgent) {
                const props = Object.assign(Object.assign({}, pick(existingAgent, agentPropertiesEditable)), pick(formData, agentPropertiesEditable));
                if (props.color === "") {
                    props.color = null;
                }
                yield agentsStore.editUser(existingAgent._id, props, formData.departments);
                userSaved = true;
                // Handle chats access changes
                const existingChatsAccess = (existingAgent.chatsAccess || []).filter((access) => {
                    return channels.accounts.some((acc) => {
                        return acc.accountId === access.accountId && acc.channel === access.name;
                    });
                });
                const newChatsAccess = formData.chatsAccess || [];
                if (!isEqual(existingChatsAccess, newChatsAccess)) {
                    const areRulesEqual = (rule1, rule2) => {
                        if (!!rule1.samePersonChats !== !!rule2.samePersonChats)
                            return false;
                        const type1 = [...rule1.type].sort();
                        const type2 = [...rule2.type].sort();
                        if (!isEqual(type1, type2))
                            return false;
                        return true;
                    };
                    const originalChatMap = new Map(existingChatsAccess.map((c) => [`${c.accountId}-${c.id}`, c]));
                    const newChatMap = new Map(newChatsAccess.map((c) => [`${c.accountId}-${c.id}`, c]));
                    const toRemove = existingChatsAccess.filter((c) => {
                        const newC = newChatMap.get(`${c.accountId}-${c.id}`);
                        if (!newC) {
                            return true;
                        }
                        return !areRulesEqual(c, newC);
                    });
                    const toAdd = newChatsAccess.filter((c) => {
                        const oldC = originalChatMap.get(`${c.accountId}-${c.id}`);
                        if (!oldC) {
                            return true;
                        }
                        return !areRulesEqual(c, oldC);
                    });
                    for (const item of toRemove) {
                        yield agentsStore.updateUserChatAccess(existingAgent._id, {
                            remove: pick(item, ["name", "accountId", "id"]),
                        });
                    }
                    for (const item of toAdd) {
                        yield agentsStore.updateUserChatAccess(existingAgent._id, {
                            add: item,
                        });
                    }
                }
                message.success(React.createElement(Trans, { i18nKey: "agentEditSuccess", values: { name: props.displayName } }));
            }
            else {
                const newUser = yield agentsStore.createUser(Object.assign(Object.assign({}, pick(formData, agentPropertiesEditable)), { color: formData.color ? formData.color : null }), formData.departments);
                userSaved = true;
                if (((_a = formData.chatsAccess) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                    for (const rule of formData.chatsAccess) {
                        yield agentsStore.updateUserChatAccess(newUser._id, {
                            add: rule,
                        });
                    }
                }
                message.success(React.createElement(Trans, { i18nKey: "agentCreateSuccess", values: { name: formData.displayName } }));
            }
        }
        catch (error) {
            console.error("Failed to save agent:", error);
            message.error(React.createElement(Trans, { i18nKey: userSaved ? "agentChatAccessSaveError" : "agentSaveError" }));
            throw error;
        }
        finally {
            setLoading(false);
        }
    });
    const handleDelete = (agent) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            yield agentsStore.deleteUser(agent._id);
            message.success(React.createElement(Trans, { i18nKey: "agentDeleteSuccess", values: { name: getName(agent) } }));
        }
        catch (error) {
            console.error("Failed to delete agent:", error);
            message.error(React.createElement(Trans, { i18nKey: "agentDeleteError" }));
            throw error;
        }
        finally {
            setLoading(false);
        }
    });
    const canDelete = agentsStore.hasPermissions([Permissions.Permission.users_manage]);
    return (React.createElement(ManagerDetailsView, { entity: entity, entityId: entityId, operation: operation, form: AgentForm, loading: loading, initialLoading: !agentsStore.users.length, readOnly: (entity) => !!entity.disabled, renderDeleteQuestion: renderDeleteQuestion, onEntitySave: handleSave, onEntityDelete: canDelete ? handleDelete : undefined, onCancel: onCancel, onSaveCompleted: onCompleted, onDeleteCompleted: onCompleted }));
});
export default AgentsDetailsView;
